import Key from "csi/core/Key";
import AddPath from "csi/def/AddPath";
import PreferencePath from "csi/def/PreferencePath";
import TableRelationshipConstructor from "csi/table/Relationship";
import Widget from "csi/table/Widget";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import ProductType from "./ProductType";
import { C8AttributeId, C8Node } from "src/c8-platform/type/node";
import { C8AttributeKey } from "src/c8-platform/type/value";

const ProductSkus = declare(Widget, {

	/**
	 * Method to build the SKU views according to the allocation configuration. 
	 * @param view 
	 * @param isAllocated 
	 * @param product 
	 */
	buildProductSkusView(view, isAllocated, product) {
		view.m_depPaths = ["Child:__Parent__/Child:ProductType;ProductColors"];
		const relationship = view.m_relationships[0] || new TableRelationshipConstructor();
		const stylePath = "Child:ForProductSource/Child:ColorSources/Parent:ColorwayColorSources/Parent:RealizedColor";
		const materialPath = "Child:ForProductSource/Child:ColorSources/Parent:ColorwayColorSources(ColorMaterial)/Parent:RealizedColor(SKUMaterial)";
		if (isAllocated) {
			relationship.m_addPaths = [lang.mixin(new AddPath(), {
				m_path: product?.$Type === "Material" ? materialPath : stylePath,
			})];
			relationship.m_typeNode = product?.$Type === "Material" ? "SKUMaterial" : "SKU" ;
		} else {
			if (product?.$Type === "DataPackage") {
				relationship.m_addPaths = [lang.mixin(new AddPath(), {
					m_path: "Child:__Parent__/Child:RealizedProducts",
				})];

				relationship.m_prefPaths = [
					Object.assign(new PreferencePath(), {
						m_path: "Child:__Parent__",
					}),
				];
			} else {
				relationship.m_id ||= "RealizedProducts";
			}

			relationship.m_sorts ||= [{ id: "RealizedColor" }];
		}

		view.m_relationships = [relationship];
	},

	/**
	 * @override
	 * mark Color/Size columns to be skipped based on Material type configuration
	 */
	onViewResolveKeysToSkip(skipSet: Set<C8AttributeKey>): void { 
		const product = this._getProduct();
	
		const _skip = (id: C8AttributeId, path?: string): void => {
			skipSet.add(Key.untypedKeyFromParts({ id, path }));
		};

		if (!ProductType.hasColor(product)) {
			_skip("RealizedColor");
			_skip("ActiveColorways", "Child:Style");
			_skip("DefaultColor", "Child:Style");
			_skip("ProductColors", "Child:Style");
			_skip("ProductionColorways", "Child:Style");
			_skip("SampleColorways", "Child:Style");
		}

		if (!ProductType.hasSize(product)) {
			_skip("RealizedSize");
			_skip("DefaultSize", "Child:Style");
			_skip("ActualSizeRange", "Child:Style/Child:Attributes");
			_skip("ProductSizes", "Child:Style");
			_skip("SizeSetSampleSizes", "Child:Style/Child:Attributes");
		}
	},

	/**
	 * @override
	 * mark Color/Size columns to be skipped based on Material type configuration
	 */
	onViewStartBuild(): void {
		const { defView } = this;
		const product = this._getProduct();
		const isAllocated = (defView.m_id === "ProductSKUWithColorAllocation" || defView.m_id === "ProductSKUMaterialWithColorAllocation") &&
			(product.ColorAllocationStatus === "ColorAllocationStatus:Allocation WIP");

		this.buildProductSkusView(defView, isAllocated, product);
	},

	/**
	 * Return product in view
	 */
	_getProduct(): C8Node | undefined {
		let product = this.getViewNode();
		if (product?.$Type === "DataPackage") {
			product = this.cacheView.get(product.__Parent__);
		}

		return product;
	},
});

export = ProductSkus;